import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Phone, CheckCircle, ChefHatIcon } from "lucide-react";
import { useParams } from "react-router-dom";

import propertiesData from "../../../config/properties.json";

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [selectedView, setSelectedView] = useState("top");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Find the property based on ID
  useEffect(() => {
    const foundProperty = propertiesData.properties.find(
      (prop) => prop.id === id
    );
    setProperty(foundProperty);

    // Set initial images for slider
    if (foundProperty) {
      setHasLoaded(true);
    }
  }, [id]);

  // ✅ Slider controls
  const nextSlide = useCallback(() => {
    if (isAnimating || !property) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % property.images.length);
    setTimeout(() => setIsAnimating(false), 1500);
  }, [isAnimating, property]);

  const prevSlide = useCallback(() => {
    if (isAnimating || !property) return;
    setIsAnimating(true);
    setCurrentSlide(
      (prev) => (prev - 1 + property.images.length) % property.images.length
    );
    setTimeout(() => setIsAnimating(false), 1500);
  }, [isAnimating, property]);

  const goToSlide = useCallback(
    (index) => {
      if (isAnimating || index === currentSlide || !property) return;
      setIsAnimating(true);
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 1500);
    },
    [isAnimating, currentSlide, property]
  );

  // ✅ Auto-slide effect
  useEffect(() => {
    if (isAnimating || !property) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [nextSlide, isAnimating, property]);

  if (!property) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-gray-600">Property not found</div>
      </div>
    );
  }

  return (
    <div>
      {property?.status === "upcomming" ? (
        <div className="bg-gray-50 min-h-screen px-6 py-6 xl:py-8 xl:px-20 ">
          {property.images && property.images.length > 0 && (
            <div className="relative overflow-hidden rounded-xl md:h-[35rem] h-[30rem]">
              <div
                className="flex transition-transform duration-[1300ms] ease-in-out h-full"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {property.images.map((image, index) => (
                  <div
                    key={index}
                    className="relative flex-shrink-0 w-full h-full overflow-hidden"
                  >
                    <img
                      className={`h-full w-full object- brightness-75 transition-transform duration-[5000ms] `}
                      src={image}
                      alt={`${property.propertyName} - Pic ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
              >
                ‹
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
              >
                ›
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {property.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition ${
                      index === currentSlide
                        ? "bg-white"
                        : "bg-white/50 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="lg:col-span-2 space-y-4 mt-8">
            <h1 className="md:text-3xl text-2xl font-bold uppercase text-gray-900">
              {property.propertyName}
            </h1>
            <p className="flex items-center text-gray-700">
              <MapPin className="w-4 h-4 mr-2 text-[#FF5A3C]" />
              {property.location}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 min-h-screen px-6 py-6 xl:py-8 xl:px-20">
          {/* ✅ Banner Slider */}
          {property.images && property.images.length > 0 && (
            <div className="relative overflow-hidden rounded-xl md:h-[35rem] h-[30rem]">
              <div
                className="flex transition-transform duration-[1300ms] ease-in-out h-full"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {property.images.map((image, index) => (
                  <div
                    key={index}
                    className="relative flex-shrink-0 w-full h-full overflow-hidden"
                  >
                    <img
                      className={`h-full w-full object-cover brightness-75 transition-transform duration-[5000ms] ${
                        index === currentSlide ? "scale-110" : "scale-100"
                      }`}
                      src={image}
                      alt={`${property.propertyName} - Pic ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
              >
                ‹
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
              >
                ›
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {property.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition ${
                      index === currentSlide
                        ? "bg-white"
                        : "bg-white/50 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ✅ Main Content */}
          <div className="grid lg:grid-cols-3 grid-cols-1 lg:gap-x-10 gap-y-5 px-0 mt-8">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-4">
              <h1 className="md:text-3xl text-2xl font-bold uppercase text-gray-900">
                {property.propertyName}
              </h1>
              <p className="flex items-center text-gray-700">
                <MapPin className="w-4 h-4 mr-2 text-[#FF5A3C]" />
                {property.location}
              </p>

              {/* Description */}
              <section>
                <h2 className="text-2xl mt-5 font-semibold mb-2">
                  Description
                </h2>
                <div className="text-gray-700 leading-relaxed text-justify lg:text-left space-y-4">
                  {property.descriptions.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </section>

              {/* Features */}
              <section>
                <h2 className="text-2xl mt-5 font-semibold mb-3">
                  Features & Facilities
                </h2>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {property.featuresFacilities.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center bg-white border rounded-lg shadow-sm p-2"
                    >
                      <CheckCircle className="w-4 h-4 text-[#FF5A3C] mr-2" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Project Highlights */}
              {property.projectHighlights?.length > 0 && (
                <section>
                  <h2 className="text-2xl mt-5 font-semibold mb-3">
                    Project Highlights
                  </h2>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {property.projectHighlights.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center bg-white border rounded-lg shadow-sm p-2"
                      >
                        <ChefHatIcon className="w-4 h-4 text-[#FF5A3C] mr-2" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Project Specifications */}
              <section>
                <h2 className="text-2xl mt-5 font-semibold mb-3">
                  Project Specifications
                </h2>
                <div className="space-y-4">
                  {property?.projectSpecifications?.map(
                    (category, catIndex) => (
                      <div
                        key={catIndex}
                        className="bg-[#F2F3F7] p-4 rounded-2xl shadow-md border"
                      >
                        <h3 className="text-[#FF5A3C] font-semibold mb-3">
                          {category.category}
                        </h3>
                        <div className="space-y-2">
                          {category?.specifications?.map((spec, specIndex) => (
                            <div
                              key={specIndex}
                              className="flex justify-between"
                            >
                              <span className="text-gray-700 font-medium">
                                {spec.item}:
                              </span>
                              <span className="text-[#1C1C1C] text-sm text-right">
                                {spec.description}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </section>

              {/* ✅ Connectivity */}
              {property?.connectivity.length > 0 && (
                <section>
                  <h2 className="text-2xl mt-5 font-semibold mb-5">
                    Connectivity
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {property?.connectivity.map(
                      ({ distance, place }, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-white shadow-sm border rounded-lg p-3"
                        >
                          <p className="text-gray-700 font-medium capitalize">
                            {place}
                          </p>
                          <p className="font-semibold text-[#FF5A3C]">
                            {distance}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </section>
              )}

              {/* ✅ Floor Plans */}
              {(property.typicalFloorPlanImage ||
                property.isometricViewImage) && (
                <section className="bg-white shadow-md border rounded-2xl p-6 mt-8">
                  <div className="flex flex-col md:flex-row justify-between items-center">
                    <h2 className="text-2xl font-semibold mb-6 text-gray-800">
                      Floor Plan
                    </h2>
                    {/* Buttons */}
                    <div className="flex justify-center items-center gap-6 mb-6">
                      {property.typicalFloorPlanImage && (
                        <button
                          onClick={() => setSelectedView("top")}
                          className={`md:px-5 md:py-2 px-4 py-1 text-sm md:text-base rounded-full font-medium transition-all duration-300 ${
                            selectedView === "top"
                              ? "bg-[#FF5A3C] text-white shadow-md scale-105"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          Top View
                        </button>
                      )}

                      {property.isometricViewImage && (
                        <button
                          onClick={() => setSelectedView("side")}
                          className={`md:px-5 md:py-2 px-4 py-1 text-sm md:text-base rounded-full font-medium transition-all duration-300 ${
                            selectedView === "side"
                              ? "bg-[#FF5A3C] text-white shadow-md scale-105"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          Side View
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Image */}
                  <div className="flex justify-center">
                    <img
                      src={
                        selectedView === "top"
                          ? property.typicalFloorPlanImage
                          : property.isometricViewImage
                      }
                      alt={`${selectedView} floor plan`}
                      className="w-auto max-w-full h-[400px] object-contain rounded-xl border shadow-sm transition-all duration-300"
                    />
                  </div>
                </section>
              )}

              {/* Area Statements */}
              {property.areaStatements?.length > 0 && (
                <section className="mt-8">
                  <h2 className="text-2xl capitalize font-semibold mb-6 text-gray-800">
                    Area Statements
                  </h2>

                  {property.areaStatements.map((group, groupIndex) => (
                    <div key={groupIndex} className="mb-10">
                      {/* Show title only if exists */}
                      {group.title && (
                        <h3 className="text-xl font-semibold mb-3 text-gray-700">
                          {group.title}
                        </h3>
                      )}

                      {/* If using new format (with list) */}
                      {Array.isArray(group.list) && group.list.length > 0 && (
                        <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
                          <table className="w-full text-sm text-left text-gray-700">
                            <thead className="bg-gray-50 border-b border-gray-300">
                              <tr>
                                {/* Generate headers dynamically from the first item's keys */}
                                {Object.keys(group.list[0]).map(
                                  (key, index) => (
                                    <th
                                      key={index}
                                      className="px-4 py-3 font-semibold whitespace-nowrap text-gray-900 capitalize text-center"
                                    >
                                      {key === "sba"
                                        ? "SUPER BUILT UP AREA"
                                        : key === "flatNo"
                                        ? "FLAT NO / TYPE"
                                        : key
                                            .replace(/([A-Z])/g, " $1")
                                            .replace(/\b\w/g, (l) =>
                                              l.toUpperCase()
                                            )}
                                    </th>
                                  )
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {group.list.map((item, rowIdx) => (
                                <tr
                                  key={rowIdx}
                                  className="border-b border-gray-200 hover:bg-gray-50 transition"
                                >
                                  {Object.entries(item).map(
                                    ([key, value], index) => (
                                      <td
                                        key={index}
                                        className={`px-4 py-3 text-center ${
                                          key === "flatNo"
                                            ? "font-medium text-[#FF5A3C]"
                                            : ""
                                        }`}
                                      >
                                        {value || "-"}
                                      </td>
                                    )
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Fallback for old format (direct array without list) - in case any property still uses it */}
                      {!group.list &&
                        Array.isArray(group) &&
                        group.length > 0 && (
                          <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
                            <table className="w-full text-sm text-left text-gray-700">
                              <thead className="bg-gray-50 border-b border-gray-300">
                                <tr>
                                  {Object.keys(group[0]).map((key, index) => (
                                    <th
                                      key={index}
                                      className="px-4 py-3 font-semibold whitespace-nowrap text-gray-900 capitalize text-center"
                                    >
                                      {key === "sba"
                                        ? "SUPER BUILT UP AREA"
                                        : key === "flatNo"
                                        ? "FLAT NO / TYPE"
                                        : key
                                            .replace(/([A-Z])/g, " $1")
                                            .replace(/\b\w/g, (l) =>
                                              l.toUpperCase()
                                            )}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {group.map((item, rowIdx) => (
                                  <tr
                                    key={rowIdx}
                                    className="border-b border-gray-200 hover:bg-gray-50 transition"
                                  >
                                    {Object.entries(item).map(
                                      ([key, value], index) => (
                                        <td
                                          key={index}
                                          className={`px-4 py-3 text-center ${
                                            key === "flatNo" ||
                                            key === "flatType"
                                              ? "font-medium text-[#FF5A3C]"
                                              : ""
                                          }`}
                                        >
                                          {value || "-"}
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                      {/* Error state if no valid data */}
                      {(!group.list ||
                        !Array.isArray(group.list) ||
                        group.list.length === 0) &&
                        (!Array.isArray(group) || group.length === 0) && (
                          <div className="text-red-500 text-sm">
                            No area statement data available
                          </div>
                        )}
                    </div>
                  ))}
                </section>
              )}
            </div>

            {/* Right Side: Form + Map */}
            <div className="lg:sticky w-full lg:w-auto lg:top-32 space-y-6 h-fit lg:h-[80vh] overflow-y-scroll">
              {/* Inquiry Form */}
              <div className="bg-white p-6 rounded-2xl shadow-md w-full">
                <h2 className="text-lg font-semibold mb-4">
                  Fill up the <span className="text-[#FF5A3C]">Inquiry</span>{" "}
                  Form
                </h2>
                <form className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="w-full border p-2 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Your Phone"
                    className="w-full border p-2 rounded-lg"
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    className="w-full border p-2 rounded-lg"
                  />
                  <textarea
                    placeholder="Message"
                    rows="3"
                    className="w-full border p-2 rounded-lg"
                  ></textarea>
                  <button
                    type="submit"
                    className="w-full bg-[#FF5A3C] text-white py-2 rounded-lg font-medium hover:bg-red-600 transition"
                  >
                    Submit
                  </button>
                </form>
              </div>

              {/* Google Map */}
              {property.mapLocation && (
                <div className="overflow-hidden rounded-xl shadow-md">
                  <iframe
                    src={property.mapLocation}
                    width="100%"
                    height="250"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-xl"
                    title={`${property.propertyName} Location Map`}
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
